/**
 * App Catalogue — Distribution detail for ONE app.
 *
 * Everything about how this app reaches a phone, in one place: the iOS and Android
 * settings, and — for iOS redemption codes — the pool itself, so you can upload codes and
 * hand them out without going anywhere else.
 *
 * Route: /admin/app-catalogue/distribution/:appId
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  IconRefresh,
  IconDeviceFloppy,
  IconAlertTriangle,
  IconTicket,
  IconLink,
  IconBrandApple,
  IconBrandGooglePlay,
  IconArrowLeft,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  Text,
  Title,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Switch,
  Badge,
  Alert,
  Avatar,
  Divider,
  Loader,
  Tabs,
  toast,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import {
  useApplicationCatalogueService,
  useAppDistributionService,
  APP_PLATFORM,
} from '@actschurch/ccms-sdk';
import type {
  AppManifest,
  AppDistributionView,
  CatalogueLookupSet,
  UpsertDistributionRequest,
} from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';
import AppCodePool from './components/AppCodePool';

const LOOKUPS_KEY = ['admin', 'app-catalogue', 'distribution-lookups'];

export default function DistributionDetailPage() {
  const { appId = '' } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();
  const distributionService = useAppDistributionService();

  const distributionsKey = ['admin', 'app-catalogue', 'distributions', appId];

  const { data: apps = [], isLoading: appsLoading } = useQuery({
    queryKey: ['admin', 'app-catalogue', 'all'],
    queryFn: () => catalogueService.getAllApps(),
  });

  const { data: lookups } = useQuery({
    queryKey: LOOKUPS_KEY,
    queryFn: () => distributionService.getLookups(),
    staleTime: 60 * 60 * 1000, // seeded; changes only on a migration
  });

  const {
    data: distributions = [],
    isLoading: distributionsLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: distributionsKey,
    queryFn: () => distributionService.getDistributions(appId),
    enabled: Boolean(appId),
  });

  const app = apps.find((a) => a.appId === appId) ?? null;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: distributionsKey });
  };

  const back = () => navigate(ADMIN_ROUTES.APP_CATALOGUE_DISTRIBUTION);

  if (appsLoading || distributionsLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Distribution</Title>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </Stack>
    );
  }

  // A failing backend must not look like an unconfigured one.
  if (error) {
    return (
      <Stack gap="lg">
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={back}>
          All apps
        </Button>
        <Alert color="red" title="Can't reach the distribution service">
          <Text size="sm">
            {error instanceof Error ? error.message : 'The Application Catalogue did not respond.'}
          </Text>
          <Text size="sm" mt="xs">
            If this is a fresh deploy: migration 194 creates these tables, and the
            <strong> ccms-api-application-catalogue</strong> container needs redeploying.
          </Text>
          <Button variant="light" color="red" size="xs" mt="sm" onClick={refresh}>
            Retry
          </Button>
        </Alert>
      </Stack>
    );
  }

  if (!app) {
    return (
      <Stack gap="lg">
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={back}>
          All apps
        </Button>
        <Alert color="yellow" title="App not found">
          <Text size="sm">No app in the catalogue has the id "{appId}".</Text>
        </Alert>
      </Stack>
    );
  }

  const iosDistribution = distributions.find((d) => d.platformId === APP_PLATFORM.IOS);
  const androidDistribution = distributions.find((d) => d.platformId === APP_PLATFORM.ANDROID);

  // The pool only exists for a method that actually hands out codes.
  const codePool = distributions.find((d) => d.yieldsCode);

  return (
    <FadeIn>
      <Stack gap="lg">
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          onClick={back}
          style={{ alignSelf: 'flex-start' }}
        >
          All apps
        </Button>

        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <Avatar
              src={app.iconUrl || undefined}
              radius="md"
              size="lg"
              style={{ backgroundColor: app.colors?.primary || undefined }}
            >
              {app.shortName?.charAt(0) ?? '?'}
            </Avatar>
            <div>
              <Title order={2}>{app.displayName}</Title>
              <Text c="dimmed" size="sm">
                {app.appId} — how this app reaches a phone
              </Text>
            </div>
          </Group>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={refresh}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Group>

        {/* One platform at a time. Stacking both settings cards plus the code pool made
            for a very long scroll where nothing was obviously related to anything else. */}
        {lookups && (
          <Tabs defaultValue="ios">
            <Tabs.List>
              <Tabs.Tab value="ios" leftSection={<IconBrandApple size={16} />}>
                iOS
                {iosDistribution && (
                  <Badge
                    size="xs"
                    ml="xs"
                    variant="light"
                    color={iosDistribution.enabled ? 'green' : 'gray'}
                  >
                    {iosDistribution.enabled ? 'Live' : 'Hidden'}
                  </Badge>
                )}
              </Tabs.Tab>
              <Tabs.Tab value="android" leftSection={<IconBrandGooglePlay size={16} />}>
                Android
                {androidDistribution && (
                  <Badge
                    size="xs"
                    ml="xs"
                    variant="light"
                    color={androidDistribution.enabled ? 'green' : 'gray'}
                  >
                    {androidDistribution.enabled ? 'Live' : 'Hidden'}
                  </Badge>
                )}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="ios" pt="md">
              <Stack gap="lg">
                <PlatformCard
                  key={`ios-${iosDistribution?.distributionId ?? 'new'}`}
                  app={app}
                  platformId={APP_PLATFORM.IOS}
                  lookups={lookups}
                  distribution={iosDistribution}
                  onSaved={refresh}
                />
                {/* The pool sits under the platform that owns it, so uploading and
                    assigning happens where you already are. */}
                {codePool?.platformId === APP_PLATFORM.IOS && (
                  <AppCodePool
                    distribution={codePool}
                    targetLabel={`${app.displayName} — ${codePool.platformName}`}
                    onChanged={refresh}
                  />
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="android" pt="md">
              <Stack gap="lg">
                <PlatformCard
                  key={`android-${androidDistribution?.distributionId ?? 'new'}`}
                  app={app}
                  platformId={APP_PLATFORM.ANDROID}
                  lookups={lookups}
                  distribution={androidDistribution}
                  onSaved={refresh}
                />
                {codePool?.platformId === APP_PLATFORM.ANDROID && (
                  <AppCodePool
                    distribution={codePool}
                    targetLabel={`${app.displayName} — ${codePool.platformName}`}
                    onChanged={refresh}
                  />
                )}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
      </Stack>
    </FadeIn>
  );
}

/** The iOS or Android half of one app's distribution config. */
function PlatformCard({
  app,
  platformId,
  lookups,
  distribution,
  onSaved,
}: {
  app: AppManifest;
  platformId: number;
  lookups: CatalogueLookupSet;
  distribution?: AppDistributionView;
  onSaved: () => void;
}) {
  const navigate = useNavigate();
  const distributionService = useAppDistributionService();

  const isIos = platformId === APP_PLATFORM.IOS;
  const platform = lookups.platforms.find((p) => p.id === platformId);

  // The manifest is the record of what actually exists. An app with no iOS bundle id has
  // no iOS build, so there is nothing to distribute — and the backend rejects it anyway.
  const declaresPlatform = isIos
    ? Boolean(app.platforms?.ios?.bundleId)
    : Boolean(app.platforms?.android?.applicationId);

  const methods = useMemo(
    () => lookups.distributionMethods.filter((m) => m.platformId === platformId),
    [lookups, platformId]
  );

  const [methodId, setMethodId] = useState<number | null>(
    distribution?.methodId ?? methods[0]?.id ?? null
  );
  const [installUrl, setInstallUrl] = useState(distribution?.installUrl ?? '');
  const [instructions, setInstructions] = useState(distribution?.installInstructions ?? '');
  const [testerGroup, setTesterGroup] = useState(distribution?.testerGroupName ?? '');
  const [supportContact, setSupportContact] = useState(distribution?.supportContact ?? '');
  const [appVersion, setAppVersion] = useState(distribution?.appVersion ?? '');
  const [enabled, setEnabled] = useState(distribution?.enabled ?? true);
  const [lowStock, setLowStock] = useState(distribution?.lowStockThreshold ?? 5);

  const selectedMethod = methods.find((m) => m.id === methodId);
  const yieldsCode = selectedMethod?.yieldsCode ?? false;
  const requiresTesterList = selectedMethod?.requiresTesterList ?? false;

  const saveMutation = useMutation({
    mutationFn: () => {
      const request: UpsertDistributionRequest = {
        methodId: methodId!,
        installUrl: installUrl.trim() || null,
        installInstructions: instructions.trim() || null,
        testerGroupName: testerGroup.trim() || null,
        supportContact: supportContact.trim() || null,
        appVersion: appVersion.trim() || null,
        enabled,
        lowStockThreshold: lowStock,
      };
      return distributionService.upsertDistribution(app.appId, platformId, request);
    },
    onSuccess: () => {
      toast.success(`${platform?.name} distribution saved`);
      onSaved();
    },
    onError: (err: any) => toast.error('Failed to save', err?.message ?? ''),
  });

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="md">
        {/* The tab already says which platform this is — all that's worth repeating is
            WHICH build we're talking about. */}
        <Text size="xs" c="dimmed" ff="monospace">
          {isIos
            ? app.platforms?.ios?.bundleId || 'No bundle id in the manifest'
            : app.platforms?.android?.applicationId || 'No application id in the manifest'}
        </Text>

        {!declaresPlatform ? (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="No build for this platform">
            <Text size="sm">
              This app's manifest has no {isIos ? 'iOS bundle id' : 'Android application id'}, so
              there is nothing to distribute yet.
            </Text>
            <Button
              size="xs"
              variant="light"
              color="yellow"
              mt="sm"
              onClick={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_EDIT(app.appId))}
            >
              Edit the manifest
            </Button>
          </Alert>
        ) : (
          <>
            <Select
              label="How is it distributed?"
              value={methodId?.toString() ?? null}
              onChange={(v) => setMethodId(v ? Number(v) : null)}
              data={methods.map((m) => ({ value: m.id.toString(), label: prettyMethodName(m.name) }))}
              description={selectedMethod?.description ?? undefined}
            />

            {/* Android has no code pool. Say so, rather than leaving an admin hunting for
                an "upload codes" button that is never going to appear. */}
            {!isIos && !yieldsCode && (
              <Alert color="blue" icon={<IconLink size={16} />}>
                <Text size="sm">
                  Android installs from a link — Google Play has no redemption codes for free
                  apps. Paste the opt-in or download URL below.
                </Text>
              </Alert>
            )}

            {yieldsCode && (
              <Alert color="violet" icon={<IconTicket size={16} />}>
                <Text size="sm">
                  This method hands out single-use codes. Save, then upload a batch in the
                  Codes panel below — each code gets a one-tap redeem link, so nobody has to
                  type it into the App Store.
                </Text>
              </Alert>
            )}

            <TextInput
              label="Install link"
              placeholder={
                isIos
                  ? 'https://apps.apple.com/za/app/…/id123456789'
                  : 'https://play.google.com/store/apps/details?id=… or an APK URL'
              }
              value={installUrl}
              onChange={(e) => setInstallUrl(e.currentTarget.value)}
              description={
                yieldsCode
                  ? "The app's store page. Each code already gets its own redeem link, so this is optional."
                  : 'Required: this is the only way the user can install the app.'
              }
            />

            {/* App Store Connect hands you the redeem link with an EMPTY code=, so pasting
                it here is the natural thing to do — but on its own it opens a blank redeem
                screen. Rather than reject it, we use it as the template and fill each
                person's code into it. Say so, so it isn't invisible. */}
            {yieldsCode && isRedeemLink(installUrl) && (
              <Alert color="violet" icon={<IconTicket size={16} />}>
                <Text size="sm">
                  That's Apple's <strong>redeem</strong> link, not a store page. We'll use it as
                  the template and fill each person's code into it, so their button opens the
                  App Store ready to install.
                </Text>
                <Text size="xs" c="dimmed" mt={4} ff="monospace" style={{ wordBreak: 'break-all' }}>
                  {previewRedeem(installUrl)}
                </Text>
              </Alert>
            )}

            <Textarea
              label="Install instructions"
              placeholder={
                yieldsCode
                  ? 'Optional — the redeem link does the work. Add anything else they should know.'
                  : '1. Open the link on your phone. 2. Tap Install…'
              }
              value={instructions}
              onChange={(e) => setInstructions(e.currentTarget.value)}
              minRows={3}
              autosize
              description="Shown verbatim to the user on their Apps page and in the code email."
            />

            {requiresTesterList && (
              <TextInput
                label="Tester group"
                placeholder={isIos ? 'TestFlight group name' : 'Play closed-testing track'}
                value={testerGroup}
                onChange={(e) => setTesterGroup(e.currentTarget.value)}
                description="Users must be allow-listed in this group before the link works."
              />
            )}

            <Group grow>
              <TextInput
                label="Published version"
                placeholder="1.0.1"
                value={appVersion}
                onChange={(e) => setAppVersion(e.currentTarget.value)}
              />
              <TextInput
                label="Support contact"
                placeholder="it@actschurch.co.za"
                value={supportContact}
                onChange={(e) => setSupportContact(e.currentTarget.value)}
              />
            </Group>

            {yieldsCode && (
              <NumberInput
                label="Warn me when available codes drop to"
                value={lowStock}
                onChange={(v) => setLowStock(typeof v === 'number' ? v : 5)}
                min={0}
                max={1000}
                description="Apple gives you a finite number of codes. This is your cue to request more."
              />
            )}

            <Divider />

            <Switch
              label="Show on the user's Apps page"
              description="Turn this off and the app disappears from everyone's download page."
              checked={enabled}
              onChange={(e) => setEnabled(e.currentTarget.checked)}
            />

            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={!methodId}
              fullWidth
            >
              Save {platform?.name} distribution
            </Button>
          </>
        )}
      </Stack>
    </Card>
  );
}

/** `AppleRedemptionCode` → `Apple Redemption Code`. */
function prettyMethodName(name: string): string {
  return name.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/**
 * Mirrors AppleRedeem.IsRedeemLink on the backend: a redeem link is a per-code TEMPLATE,
 * not the app's store page — opened without a code it lands on an empty redeem screen.
 */
function isRedeemLink(url: string): boolean {
  const u = url.trim().toLowerCase();
  return u.includes('/redeem') || u.includes('{code}');
}

/** Show the admin what one of their users will actually get, using a sample code. */
function previewRedeem(template: string): string {
  const sample = '6LE3YNFT97JT';
  if (/\{code\}/i.test(template)) return template.replace(/\{code\}/gi, sample);
  if (/([?&])code=[^&#]*/i.test(template)) {
    return template.replace(/([?&])code=[^&#]*/i, `$1code=${sample}`);
  }
  return `${template}${template.includes('?') ? '&' : '?'}code=${sample}`;
}
